import React, { useState } from 'react';
import { Link as RouterLink, To } from 'react-router-dom';

import { AppBar, Box, Button, IconButton, Toolbar, Typography, useTheme } from '@mui/material';

import { DisplayUser } from '~/components/DisplayUser';
import { HomeIcon, InfoIcon, RefreshIcon } from '~/components/icons';
import { UpdateJiraStatus } from '~/components/JiraStatus';
import { Tooltip } from '~/components/Tooltip';
import { useLoggedUser } from '~/contexts/LoggedUserContext';
import { useMarketParam } from '~/contexts/UrlParamsContext';
import { validateVehicleImages } from '~/controllers/images';
import { SITE_CLIENT } from '~/env';
import { useJiraProjectConfig } from '~/hooks/useJiraProjectConfig';
import { useProductConfigurationQuery } from '~/hooks/useProductConfigurationQuery';
import { useRequestQuery } from '~/hooks/useRequestQuery';
import { REQUESTS_SEARCH_CACHE } from '~/pages/RequestsPage';
import { RequestsPageUrl } from '~/pages/urls';
import { useAppDispatch } from '~/redux';
import { addImageErrors, clearImageErrors, showDialog } from '~/redux/features/imageErrorsSlice';

import { RequestInfo } from './RequestInfo';

export const RequestHeader: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: request, refetch } = useRequestQuery();
  const { palette } = useTheme();
  const market = useMarketParam();
  const { data: jiraProjectConfig } = useJiraProjectConfig();

  const loggedUser = useLoggedUser();

  const {
    data: productData,
  } = useProductConfigurationQuery();

  const [isValidating, setIsValidating] = useState<boolean>(false);
  const validateOnClick = (vehicle: string | undefined, year: string | undefined) => {
    if (!vehicle || !year) {
      return;
    }

    const doAsync = async () => {
      const errors = await validateVehicleImages({ vehicle, year });
      setIsValidating(false);
      if (errors) {
        dispatch(addImageErrors(errors));
        dispatch(showDialog());
      }
    }
    setIsValidating(true);
    dispatch(clearImageErrors());
    doAsync();
  }

  return (
    <AppBar position="relative" sx={{ mb: 1 }}>
      <Toolbar>
        <Box sx={{ flex: 1 }}>
          <IconButton
            to={RequestsPageUrl.expand({ market, ...REQUESTS_SEARCH_CACHE })}
            component={
              RouterLink as React.FC<{
                value: string;
                to: To;
              }> as React.ElementType
            }
            sx={{
              p: '11px',
              background: palette.dark.main,
              borderRadius: '4px',
              '&:hover': {
                bgcolor: palette.dark.main,
              },
            }}
          >
            <HomeIcon sx={{
              width: 20,
              height: 22,
              path: {
                fill: palette.dark.contrastText,
              },
            }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', flex: 3, maxWidth: 480 }}>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
          >
            {request?.title}
          </Typography>
          {request?.title && (
            <Tooltip sx={{ p: '0', textAlign: 'revert' }} title={<RequestInfo />}>
              <InfoIcon sx={{ height: 22, width: 22, color: palette.primary.light, ml: '18px' }} />
            </Tooltip>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'flex-end', flex: 1 }}>
          {(SITE_CLIENT!=='Subaru') && 
          <UpdateJiraStatus
            dark
            valueLabel="Status: "
            notificationLabel='Request'
            issueId={request?.requestId}
            value={request?.status}
            typeName={jiraProjectConfig.requestTypeName}
            onChange={refetch}
          />}
          {(loggedUser?.validation && productData?.vehicle && productData?.year) && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              endIcon={isValidating && <RefreshIcon sx={{
                width: 12, 
                height: 12,
                animation: 'spin 2s linear infinite',
                animationDirection: 'reverse',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(360deg)',
                  },
                  '100%': {
                    transform: 'rotate(0deg)',
                  },
                },
              }} />}
              disabled={isValidating}
              onClick={() => validateOnClick(productData.vehicle, productData.year)}
              sx={{
                color: palette.dark.contrastText,
                background: palette.dark.main,
                borderRadius: '4px',
                '&:hover': {
                  bgcolor: palette.dark.main,
                },
              }}
            >
              Validate
            </Button>
          )}
          <DisplayUser />
        </Box>
      </Toolbar>
    </AppBar>
  );
};
